import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import { ReactNode } from 'react';

const AboutPage = () => {
  const SectionWrapper = ({ children, className = "" }: { children: ReactNode; className?: string }) => {
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1,
    });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Mission Section - Image Left, Content Right */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <SectionWrapper className="order-2 lg:order-1">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&h=600&fit=crop&crop=center"
                  alt="Modern farming with technology"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </SectionWrapper>
            
            <SectionWrapper className="order-1 lg:order-2">
              <div className="lg:pl-8">
                <div className="inline-flex items-center px-4 py-2 bg-foodsply-green/10 rounded-full mb-6">
                  <img src="/images/lightbulb.svg" alt="" className="w-5 h-5 mr-2" />
                  <span className="text-foodsply-green font-semibold">Our Mission</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                  Empowering Farmers Through 
                  <span className="text-foodsply-green"> Community Agriculture</span>
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  We believe that sustainable agriculture starts with empowered farmers. By leveraging blockchain technology and community-driven solutions, we're creating a transparent, efficient ecosystem where farmers can thrive while feeding the world responsibly.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-foodsply-green rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <p className="text-gray-700">Direct farmer-to-consumer connections eliminating middlemen</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-foodsply-green rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <p className="text-gray-700">Transparent supply chain tracking from farm to table</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-foodsply-green rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <p className="text-gray-700">Fair pricing and immediate payments through blockchain</p>
                  </div>
                </div>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </section>

      {/* Vision Section - Content Left, Image Right */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <SectionWrapper>
              <div className="lg:pr-8">
                <div className="inline-flex items-center px-4 py-2 bg-foodsply-orange/10 rounded-full mb-6">
                  <img src="/images/earth.svg" alt="" className="w-5 h-5 mr-2" />
                  <span className="text-foodsply-orange font-semibold">Our Vision</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                  A Frictionless Food 
                  <span className="text-foodsply-orange"> Supply Chain</span>
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Imagine a world where fresh, healthy food moves seamlessly from farm to your table. Where farmers are fairly compensated, consumers pay honest prices, and every step is transparent and sustainable. This is the future we're building.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-2xl shadow-sm">
                    <img src="/images/bar-chart-orange.svg" alt="" className="w-8 h-8 mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">Increased Efficiency</h3>
                    <p className="text-gray-600 text-sm">Reducing waste and costs through optimized logistics</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl shadow-sm">
                    <img src="/images/shield.svg" alt="" className="w-8 h-8 mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">Food Security</h3>
                    <p className="text-gray-600 text-sm">Ensuring reliable access to nutritious food globally</p>
                  </div>
                </div>
              </div>
            </SectionWrapper>
            
            <SectionWrapper>
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&h=600&fit=crop&crop=center"
                  alt="Global food supply chain visualization"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </section>

      {/* What We Do Section - Image Left, Content Right */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <SectionWrapper className="order-2 lg:order-1">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae?w=800&h=600&fit=crop&crop=center"
                  alt="Farmer using technology in greenhouse"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </SectionWrapper>
            
            <SectionWrapper className="order-1 lg:order-2">
              <div className="lg:pl-8">
                <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full mb-6">
                  <img src="/images/users-group.svg" alt="" className="w-5 h-5 mr-2" />
                  <span className="text-blue-600 font-semibold">What We Do</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                  Connecting Farmers to
                  <span className="text-blue-600"> Consumers Directly</span>
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  We provide farmers with the tools, training, and technology they need to succeed in the digital marketplace. By eliminating intermediaries, we ensure that cost savings flow directly to consumers while farmers receive fair compensation for their hard work.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-lg">1</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Farmer Education & Onboarding</h3>
                      <p className="text-gray-600">Comprehensive training on digital tools, sustainable practices, and market access</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-lg">2</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Marketplace Platform</h3>
                      <p className="text-gray-600">Secure, transparent platform for direct trading with built-in logistics support</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-lg">3</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Cost Savings Distribution</h3>
                      <p className="text-gray-600">Automated systems to pass eliminated middleman costs to consumers</p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </section>

      {/* Impact Section - Content Left, Image Right */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <SectionWrapper>
              <div className="lg:pr-8">
                <div className="inline-flex items-center px-4 py-2 bg-green-50 rounded-full mb-6">
                  <img src="/images/bar-chart.svg" alt="" className="w-5 h-5 mr-2" />
                  <span className="text-green-600 font-semibold">Our Impact</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                  Transforming Lives Through
                  <span className="text-green-600"> Technology</span>
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Every transaction on our platform creates positive change. From improving farmer livelihoods to reducing food waste and making healthy food more affordable, we're building a better food system for everyone.
                </p>
                <div className="grid sm:grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                    <div className="text-3xl font-bold text-foodsply-green mb-2">15%</div>
                    <p className="text-gray-600">Increase in farmer income</p>
                  </div>
                  <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                    <div className="text-3xl font-bold text-foodsply-orange mb-2">25%</div>
                    <p className="text-gray-600">Reduction in food waste</p>
                  </div>
                  <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                    <div className="text-3xl font-bold text-blue-600 mb-2">40%</div>
                    <p className="text-gray-600">Lower consumer prices</p>
                  </div>
                  <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                    <div className="text-3xl font-bold text-green-600 mb-2">100%</div>
                    <p className="text-gray-600">Supply chain transparency</p>
                  </div>
                </div>
              </div>
            </SectionWrapper>
            
            <SectionWrapper>
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=600&fit=crop&crop=center"
                  alt="Happy farmers in vegetable market"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-foodsply-green to-foodsply-orange text-white">
        <div className="container mx-auto px-4">
          <SectionWrapper className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Ready to Transform Agriculture?
            </h2>
            <p className="text-xl mb-8 opacity-90 leading-relaxed">
              Join thousands of farmers and consumers who are already part of the FoodSply revolution. 
              Together, we're building a more sustainable, transparent, and equitable food system.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={() => window.location.href = '/waitlist'}
                className="group bg-white text-foodsply-green px-12 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center text-lg"
              >
                Join Waitlist
                <img src="/images/arrow-right-1.svg" alt="" className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </SectionWrapper>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AboutPage;